import type { RouteDef } from '../router';
import { trialCtaHtml } from '../trial-cta';
import { countryDisplayName } from '../utils/country-name';
import { allCountries, getTopTourism } from '../data/prices';
import { t, getLocale } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { pricesPageHtml, priceRowHtml, sortPriceRows, type Country, type PriceRow, type PricesSort } from '@roamzy/shared';

/**
 * Browser-native country-name localizer. Given an ISO 3166-1 alpha-2
 * (e.g. 'FR') and a locale ('ru'), returns the country name in that
 * locale ('Франция'). Used to power locale-aware search — operator
 * 2026-05-09: "пользователь ищет название страны на своей локали".
 *
 * `Intl.DisplayNames` ships with every modern browser (iOS 14.1+,
 * Chrome 81+, Safari 14.1+) — we don't ship any translation tables
 * ourselves. Built once per locale; cached so 193 rows × 5 locales
 * doesn't re-instantiate the heavy ICU lookup.
 */
const displayNamesCache: Partial<Record<string, Intl.DisplayNames | null>> = {};
function getDisplayNames(locale: string): Intl.DisplayNames | null {
  if (locale in displayNamesCache) return displayNamesCache[locale] ?? null;
  let dn: Intl.DisplayNames | null = null;
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      dn = new Intl.DisplayNames([locale], { type: 'region' });
    }
  } catch {
    dn = null;
  }
  displayNamesCache[locale] = dn;
  return dn;
}

function localizedCountryName(c: Country, dn: Intl.DisplayNames | null): string | null {
  if (!dn || !c.isoAlpha2) return null;
  try {
    const name = dn.of(c.isoAlpha2);
    return name && name !== c.isoAlpha2 ? name : null;
  } catch {
    return null;
  }
}

type Row = PriceRow & Country;

/** A catalog country as the list prints it: the name in the page language. */
const toRow = (c: Country): Row => ({ ...c, name: countryDisplayName(c), nameEn: c.name });

/** The list in one of the four orders; the server paints 'popular' the same way. */
function sortedRows(mode: PricesSort): Row[] {
  return sortPriceRows(allCountries.map(toRow), mode, getTopTourism().map((c) => c.slug), getLocale());
}

const prices: RouteDef = {
  path: '/prices',
  routeType: 'prices',
  navKey: 'prices',
  metaTitle: `eSIM Prices — All ${allCountries.length} Countries | Roamzy`,
  metaDesc: `Live per-megabyte rates for ${allCountries.length} countries. Pay only for data used. No bundles, no expiry. USDT or USDC payments.`,
  // fix-2: prices schema v1
  render() {
    // One template with the server (shared/prices-page.ts), the list already
    // painted in the default order: the redraw of the server's page changes
    // nothing, and the list no longer arrives empty until mount (2026-09-30).
    return pricesPageHtml({
      locale: getLocale(),
      rows: sortedRows('popular'),
      t: (key, vars) => t(key as TranslationKey, vars),
      trialCtaHtml: trialCtaHtml(),
    });
  },
  mount(root) {
    const list = root.querySelector<HTMLElement>('#priceList');
    const search = root.querySelector<HTMLInputElement>('#priceSearch');
    const sortBtns = root.querySelectorAll<HTMLButtonElement>('.sort-btn');
    if (!list) return;

    let sortMode: PricesSort = 'popular';
    let query = '';

    // Honor ?q= on load: the home-page WebSite JSON-LD advertises a
    // SearchAction with urlTemplate `/prices?q={search_term_string}` — until
    // 2026-07-13 this parameter was silently ignored, i.e. the schema lied to
    // machines (Google sitelinks searchbox, agents). Prefill the box + filter.
    const qParam = new URLSearchParams(window.location.search).get('q');
    if (qParam && search) {
      search.value = qParam;
      query = qParam.trim().toLowerCase();
    }

    // Build a per-row "haystack" combining the catalog name (English) with
    // the localized name from Intl.DisplayNames (e.g. "Франция" for the
    // current RU locale). Cached per render so we don't re-call .of() for
    // every keystroke. Operator 2026-05-09: search must work in the
    // user's language, not just English.
    const dn = getDisplayNames(getLocale());
    const searchHaystack = new Map<string, string>();
    function haystackFor(c: Row): string {
      const cached = searchHaystack.get(c.slug);
      if (cached !== undefined) return cached;
      const en = c.nameEn.toLowerCase();
      const localized = (localizedCountryName(c, dn) ?? '').toLowerCase();
      // The name on the row can differ from CLDR ("ДР Конго", 中国台湾), so
      // it is searchable too; CLDR's form still matches ("Киншаса").
      const shown = c.name.toLowerCase();
      // Join with a separator so "ran" matches "France" via any path
      // without false-positives across the boundary.
      const combined = [...new Set([en, shown, localized].filter(Boolean))].join('\n');
      searchHaystack.set(c.slug, combined);
      return combined;
    }

    function paint(): void {
      const filtered = sortedRows(sortMode).filter((c) =>
        query ? haystackFor(c).includes(query) : true,
      );
      list!.innerHTML = filtered.map((c) => priceRowHtml(c, getLocale())).join('');
    }

    paint();

    // Debounce search 120ms — every keystroke rebuilds 193 rows via
    // innerHTML, which is fine on desktop but stutters on mobile when
    // combined with scroll. 120ms is below the "feels delayed" threshold
    // (~150ms) but consolidates rapid typing into one repaint per word.
    let searchTimer: number | null = null;
    const onSearch = (): void => {
      if (searchTimer !== null) window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        query = (search?.value ?? '').trim().toLowerCase();
        paint();
        searchTimer = null;
      }, 120);
    };
    const onSort = (e: Event): void => {
      const btn = e.currentTarget as HTMLButtonElement;
      sortBtns.forEach((b) => b.classList.remove('sort-btn--active'));
      btn.classList.add('sort-btn--active');
      sortMode = (btn.dataset.sort as PricesSort) || 'az';
      paint();
    };

    search?.addEventListener('input', onSearch);
    sortBtns.forEach((b) => b.addEventListener('click', onSort));

    return () => {
      search?.removeEventListener('input', onSearch);
      sortBtns.forEach((b) => b.removeEventListener('click', onSort));
    };
  },
};

export default prices;
